import styled from 'styled-components';
import { P, fs, radius, eyebrowLabel, onPhone, tint, tintB, tintT } from '../../theme/tokens';

export const QueueNote = styled.span`
  flex: 1 1 220px;
  font-size: ${fs.sm}px;
  color: ${P.subtle};
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  > span {
    ${eyebrowLabel}
  }
`;

export const Dot = styled.span`
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => p.$color};
`;

export const BandLine = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => p.$color};

  em {
    font-style: normal;
    font-weight: 500;
    color: ${P.subtle};
  }
`;

export const Below = styled.div`
  margin-bottom: 20px;
`;

export const ModalBody = styled.div`
  max-height: 66vh;
  overflow-y: auto;
  padding: 20px;
`;

export const Eyebrow = styled.div`
  ${eyebrowLabel}
`;

export const ScoreHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;

  strong {
    display: block;
    font-size: 40px;
    font-weight: 700;
    line-height: 1;
    color: ${(p) => p.$color};
    font-variant-numeric: tabular-nums;
  }
  aside {
    text-align: right;
  }
  aside small {
    display: block;
    margin-top: 5px;
    font-size: 11px;
    color: ${P.subtle};
  }
`;

export const Signals = styled.div`
  margin-top: 14px;
  border-top: 1px solid ${P.line};

  > header {
    padding: 12px 0 8px;
  }
`;

export const SignalRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${P.line};

  > div > div {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${fs.sm}px;
    font-weight: 600;
  }
  p {
    margin: 3px 0 0;
    font-size: 11px;
    line-height: 1.45;
    color: ${P.subtle};
  }
`;

export const Weight = styled.span`
  flex: none;
  font-size: ${fs.sm}px;
  font-weight: 700;
  color: ${(p) => p.$color};
  font-variant-numeric: tabular-nums;
`;

export const Total = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-top: 10px;
  margin-top: 4px;
  font-size: ${fs.sm}px;
  font-weight: 700;
`;

export const Model = styled.div`
  margin-top: 16px;
  border: 1px solid ${P.gray200};
  border-radius: ${radius.lg}px;
  overflow: hidden;

  > button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 12px 14px;
    border: none;
    background: ${P.bgSubtle};
    font-family: inherit;
    text-align: left;
    cursor: pointer;
  }
  > button:focus-visible {
    outline: 2px solid ${P.navy};
    outline-offset: -2px;
  }
  > button strong {
    display: block;
    font-size: ${fs.sm}px;
    font-weight: 700;
    color: ${P.ink};
  }
  > button small {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: ${P.subtle};
  }
  > button svg {
    font-size: 11px;
    color: ${P.subtle};
  }
`;

export const ModelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 6px;
  font-size: 11px;
  color: ${P.subtle};
`;

export const Factor = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr auto;
  gap: 10px;
  align-items: flex-start;
  padding: 6px 14px;

  > button,
  > span:first-child {
    padding: 0;
    border: none;
    background: none;
    line-height: 1.4;
    font-size: 13px;
    color: ${(p) => (p.$on ? P.success : P.faint)};
  }
  > button {
    cursor: pointer;
  }
  > button:focus-visible {
    outline: 2px solid ${P.navy};
    outline-offset: 2px;
  }
  strong {
    display: block;
    font-size: ${fs.xs}px;
    font-weight: 600;
  }
  p {
    margin: 2px 0 0;
    font-size: 11px;
    line-height: 1.45;
    color: ${P.subtle};
  }
`;

export const Hollow = styled.span`
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 1.5px solid ${P.faint};
  border-radius: 50%;
  vertical-align: -2px;
`;

export const Footnote = styled.p`
  margin: 4px 0 0;
  padding: 12px 14px 4px;
  border-top: 1px solid ${P.line};
  font-size: 11px;
  line-height: 1.5;
  color: ${P.subtle};
`;

export const Clamped = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: ${radius.md}px;
  background: color-mix(in oklch, ${P.warning} 10%, white);
  border: 1px solid color-mix(in oklch, ${P.warning} 28%, white);
  font-size: ${fs.xs}px;
  color: ${tintT(P.warning)};
`;

export const Divided = styled.section`
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid ${P.line};

  h3 {
    margin: 0 0 12px;
    font-size: ${fs.base}px;
    font-weight: 700;
  }
`;

export const Days = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 14px;
`;

const choice = (on) => `
  border: 1px solid ${on ? P.navy : P.gray200};
  border-radius: ${radius.md}px;
  background: ${on ? P.grad : P.white};
  color: ${on ? P.white : P.ink};
  font-family: inherit;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${P.navy};
    outline-offset: 2px;
  }
`;

export const Day = styled.button`
  ${(p) => choice(p.$on)}
  flex: none;
  min-width: 64px;
  padding: 8px 10px;
  text-align: center;

  b {
    display: block;
    font-size: 15px;
    font-weight: 700;
  }
  span {
    display: block;
    font-size: 10px;
    letter-spacing: 0.06em;
  }
  small {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    opacity: 0.75;
  }
`;

export const Slots = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
`;

export const Slot = styled.button`
  ${(p) => choice(p.$on)}
  height: 36px;
  font-size: ${fs.xs}px;
  font-weight: 600;
`;

export const BookRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;

  > span {
    font-size: ${fs.xs}px;
    color: ${P.subtle};
  }
`;

export const FactGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin: 20px 0 0;
  padding-top: 18px;
  border-top: 1px solid ${P.line};

  dt {
    ${eyebrowLabel}
  }
  dd {
    margin: 3px 0 0;
    font-size: ${fs.sm}px;
    font-weight: 600;
  }
  select {
    min-width: 0;
    margin-top: 3px;
    padding-top: 7px;
    padding-bottom: 7px;
  }
`;

export const ConvertBox = styled.section`
  margin-top: 20px;
  padding: 16px 18px;
  border-radius: ${radius.lg}px;
  border: 1px solid ${(p) => (p.$done ? tintB(P.success) : P.gray200)};
  background: ${(p) => (p.$done ? tint(P.success) : P.bgSubtle)};

  > header {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 10px;
  }
  h3 {
    margin: 0;
    font-size: ${fs.base}px;
    font-weight: 700;
  }
  > header span,
  .hint {
    font-size: ${fs.xs}px;
    color: ${P.subtle};
  }
  .hint {
    flex: 1 1 170px;
  }
  select {
    min-width: 0;
    margin-top: 6px;
  }
`;

export const ConvertGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
`;

export const ConvertActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
`;

export const Done = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;

  > span {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${P.success};
    color: ${P.white};
    font-size: 15px;
  }
  strong {
    display: block;
    font-size: 15px;
    font-weight: 700;
  }
  small {
    display: block;
    margin-top: 3px;
    font-size: ${fs.xs}px;
    color: ${P.muted};
  }
`;

export const DoneSteps = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;

  svg {
    font-size: 9px;
    margin-right: 6px;
  }
`;

export const LinkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: ${fs.sm}px;
  font-weight: 600;
  color: ${tintT(P.success)};
  cursor: pointer;

  svg {
    font-size: 11px;
  }
  &:hover {
    text-decoration: underline;
  }
`;

export const Drill = styled.div`
  margin-top: 16px;

  > div:first-child {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 7px;
  }
  > div:first-child span {
    ${eyebrowLabel}
    font-size: 10px;
    letter-spacing: 0.12em;
    color: rgba(255, 255, 255, 0.6);
  }
  > div:first-child b {
    font-size: ${fs.xs}px;
    font-weight: 600;
    color: ${P.white};
  }
`;

export const DrillBar = styled.div`
  height: 6px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: ${radius.pill}px;
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    width: ${(p) => p.$pct}%;
    background: ${P.white};
    border-radius: inherit;
    transition: width 200ms;
  }
`;

export const Rail = styled.div`
  position: sticky;
  top: -16px;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: -16px -18px 0;
  padding: 12px 22px;
  border-bottom: 1px solid ${P.line};
  background: ${P.bgSubtle};
`;

export const RailChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${radius.pill}px;
  border: 1px solid ${(p) => (p.$done ? tintB(P.success) : P.gray200)};
  background: ${(p) => (p.$done ? tint(P.success) : P.white)};
  color: ${(p) => (p.$done ? tintT(P.success) : P.muted)};
  font-size: 11px;
  font-weight: 600;

  b {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    opacity: 0.75;
  }
`;

export const SectionStack = styled.div`
  display: grid;
  gap: 18px;
  padding: 6px 4px 8px;
`;

export const IntakeSection = styled.section`
  padding: 16px 18px;
  border-radius: ${radius.lg}px;
  border: 1px solid ${(p) => (p.$restricted ? tintB(P.purple) : P.line)};
  background: ${(p) => (p.$restricted ? tint(P.purple) : P.white)};

  > header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: ${(p) => (p.$collapsed ? 0 : 14)}px;
  }
  > header h3 {
    margin: 0;
    font-size: ${fs.base}px;
    font-weight: 700;
  }
  > header small {
    margin-left: auto;
    font-size: 11px;
    color: ${P.subtle};
  }
  > header button {
    padding: 0;
    border: none;
    background: none;
    font-family: inherit;
    font-size: ${fs.xs}px;
    font-weight: 600;
    color: ${P.navyInk};
    cursor: pointer;
  }
  > header button:focus-visible {
    outline: 2px solid ${P.navy};
    outline-offset: 2px;
  }
`;

export const Blocked = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px;
  border: 1px dashed ${P.gray200};
  border-radius: 10px;
  background: ${P.bgSubtle};
  font-size: ${fs.xs}px;
  line-height: 1.5;
  color: ${P.subtle};

  svg {
    flex: none;
    font-size: 14px;
  }
`;

export const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  ${onPhone} {
    grid-template-columns: 1fr;
  }
`;

export const IntakeField = styled.div`
  grid-column: ${(p) => (p.$wide ? '1 / -1' : 'auto')};
  min-width: 0;

  > header {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 6px;
  }
  > header label,
  > header > span:first-child {
    font-size: ${fs.xs}px;
    font-weight: 600;
    color: ${P.ink};
  }
  > p {
    margin: -2px 0 7px;
    font-size: 11px;
    line-height: 1.45;
    color: ${P.subtle};
  }
  select {
    min-width: 0;
  }
`;

export const Range = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 8px;
  align-items: center;

  span {
    font-size: ${fs.xs}px;
    color: ${P.subtle};
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Chip = styled.button`
  padding: 6px 12px;
  border-radius: ${radius.pill}px;
  border: 1px ${(p) => (p.$dashed ? 'dashed' : 'solid')} ${(p) => (p.$on ? P.navy : p.$dashed ? P.faint : P.gray200)};
  background: ${(p) => (p.$on ? P.grad : P.white)};
  color: ${(p) => (p.$on ? P.white : p.$dashed ? P.subtle : P.ink)};
  font-family: inherit;
  font-size: ${fs.xs}px;
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${P.navy};
    outline-offset: 2px;
  }
`;

export const Computed = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
  padding: 10px 12px;
  border-radius: ${radius.md}px;
  border: 1px solid ${P.gray200};
  background: ${P.bgSubtle};

  strong {
    font-size: ${fs.sm}px;
    font-weight: 700;
  }
  span {
    font-size: 11px;
    color: ${P.subtle};
  }
`;

export const FieldNote = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: ${radius.md}px;
  background: ${tint(P.info)};
  font-size: 11px;
  line-height: 1.45;
  color: ${tintT(P.info)};
`;

export const IntakeFoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;

  > span {
    flex: 1 1 200px;
    font-size: 11px;
    color: ${P.subtle};
  }
  button {
    height: 38px;
  }
`;
